import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spin } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import SubPageLayout from '../helpers/SubPageLayout';
import FormSection from '../helpers/FormSection';
import FormField from '../helpers/FormField';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNewSite,
  clearSiteEditing,
  fetchSiteById,
  getAddNewSiteLoading,
  getAvailableUsersForTenant,
  getEditingSite,
  getUsersForTenant,
  getUsersForTenantLoading,
} from '../../../features/site.store';
import { Form, Formik } from 'formik';
import { AvailableUsers, CompanySelectDropDown } from '../helpers/drop-downs-for-enums';
import { Uploader } from '../helpers/Components';
import get from 'lodash.get';
import to from '../../../utils/to';
import { toast } from 'react-toastify';
import eng from '../../../utils/eng';
import { getUser, getIsSuperAdmin, getIsAdminApi } from '../../../features/auth.store';
import { ROLE } from '../../../utils/features';

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope =
    typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(
    new Set([
      ...splitScope,
      ...(role ? [role] : []),
      ...(Array.isArray(roles) ? roles : []),
      ...authorityNames,
    ].filter(Boolean)),
  );
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const hasMinearcAuthority = (user) => {
  const roles = normalizeRoles(user);
  return roles.some((r) =>
    [
      'ROLE_TYPE_MINEARC_ROLE',
      'ROLE_MINE_ARC_USER',
      'ROLE_MINE_ARC_SERVICE_TECHNICIAN',
      'ROLE_MINE_ARC_SERVICE_COORDINATOR',
      ROLE.MINEARC,
    ].includes(r),
  );
};

const SitesAdd = () => {
  const formRef = useRef();
  const imageRef = useRef();
  const dispatch = useDispatch();
  const history = useHistory();
  const [showForm, setShowForm] = useState(false);

  const { id } = useParams();

  const user = useSelector(getUser);
  const isSuperAdmin = useSelector(getIsSuperAdmin);
  const isAdminApi = useSelector(getIsAdminApi);

  const editingSite = useSelector(getEditingSite);
  const addNewSiteLoading = useSelector(getAddNewSiteLoading);
  const usersForTenant = useSelector(getUsersForTenant) || [];
  const usersForTenantLoading = useSelector(getUsersForTenantLoading);

  const isMinearcRole = useMemo(() => hasMinearcAuthority(user), [user]);
  const isSuperAdminRole = useMemo(() => hasRole(user, ROLE.SA), [user]);

  const hasGlobalTenantAccess = useMemo(
    () => isSuperAdmin || isSuperAdminRole || isMinearcRole || isAdminApi,
    [isSuperAdmin, isSuperAdminRole, isMinearcRole, isAdminApi],
  );

  const [contactTenantId, setContactTenantId] = useState(undefined);

  // The available-users API omits users already assigned as contacts.
  const contactUsers = useMemo(() => {
    const currentContacts =
      editingSite && contactTenantId === editingSite.tenantIdentifier
        ? ['mainContact', 'secondContact', 'thirdContact', 'fourthContact']
            .map((key) => editingSite[key])
            .filter((contact) => contact?.id)
        : [];

    return Array.from(
      new Map([...currentContacts, ...usersForTenant].map((u) => [String(u.id), u])).values(),
    );
  }, [editingSite, contactTenantId, usersForTenant]);

  const [filteredUsersForTenant, setFilteredUsers] = useState({
    mainContact: { list: [], disabled: false },
    secondContact: { list: [], disabled: false },
    thirdContact: { list: [], disabled: false },
    fourthContact: { list: [], disabled: false },
  });

  useEffect(() => {
    if (editingSite && editingSite.tenantIdentifier) {
      setContactTenantId(editingSite.tenantIdentifier);
      dispatch(getAvailableUsersForTenant({ tenantId: editingSite.tenantIdentifier }));
    }
  }, [editingSite, dispatch]);

  useEffect(() => {
    (async () => {
      if (id) {
        dispatch(clearSiteEditing());
        await dispatch(fetchSiteById({ id }));
      }
      setShowForm(true);
    })();
  }, [id, dispatch]);

  useEffect(() => {
    setFilteredUsers({
      mainContact: { list: contactUsers, disabled: false },
      secondContact: { list: contactUsers, disabled: false },
      thirdContact: { list: contactUsers, disabled: false },
      fourthContact: { list: contactUsers, disabled: false },
    });
  }, [contactUsers]);

  const filterRestUsers = (v, key) => {
    const values = formRef.current?.values || {};
    const { mainContact, secondContact, thirdContact, fourthContact } = values;
    const s = { mainContact, secondContact, thirdContact, fourthContact, [key]: v };
    const f = (arr) => contactUsers.filter(({ id }) => arr.indexOf(String(id)) === -1);

    setFilteredUsers({
      mainContact: { list: f([s.secondContact, s.thirdContact, s.fourthContact]), disabled: false },
      secondContact: { list: f([s.mainContact, s.thirdContact, s.fourthContact]), disabled: !s.mainContact },
      thirdContact: { list: f([s.mainContact, s.secondContact, s.fourthContact]), disabled: !s.secondContact },
      fourthContact: { list: f([s.mainContact, s.secondContact, s.thirdContact]), disabled: !s.thirdContact },
    });
  };

  const handleSave = async (value) => {
    const image = get(imageRef, ['current', 'fileList', 0, 'originFileObj'], null);
    let err;

    const {
      name,
      address,
      tenantIdentifier,
      mainContact,
      secondContact,
      thirdContact,
      fourthContact,
    } = value;

    const formValue = {
      name,
      address,
      tenantIdentifier,
      mainContactUserId: mainContact ? parseInt(mainContact, 10) : null,
      secondContactUserId: secondContact ? parseInt(secondContact, 10) : null,
      thirdContactUserId: thirdContact ? parseInt(thirdContact, 10) : null,
      fourthContactUserId: fourthContact ? parseInt(fourthContact, 10) : null,
    };

    if (id) {
      [err] = await to(
        dispatch(addNewSite({ formValue: { ...formValue, id }, image, isEditing: true })).unwrap(),
      );
    } else {
      [err] = await to(dispatch(addNewSite({ formValue, image, isEditing: false })).unwrap());
    }

    if (err) {
      return toast.error(err.err || eng.somethingWentWrong);
    }

    history.push('/sites');
    toast.success(id ? eng.toast.updateSiteSuccessfully : eng.toast.addSiteSuccessfully);
  };

  let editingSiteInitValue = {};
  if (editingSite) {
    editingSiteInitValue = {
      name: editingSite.name || '',
      address: editingSite.address || '',
      tenantIdentifier: editingSite.tenantIdentifier || undefined,
      mainContact: editingSite.mainContact?.id ? String(editingSite.mainContact.id) : undefined,
      secondContact: editingSite.secondContact?.id ? String(editingSite.secondContact.id) : undefined,
      thirdContact: editingSite.thirdContact?.id ? String(editingSite.thirdContact.id) : undefined,
      fourthContact: editingSite.fourthContact?.id ? String(editingSite.fourthContact.id) : undefined,
    };
  }

  return (
    <SubPageLayout title={id ? 'Edit Site' : 'Add Sites'} loading={addNewSiteLoading}>
      {showForm ? (
        <Formik
          initialValues={id ? editingSiteInitValue : initialValue}
          enableReinitialize
          onSubmit={handleSave}
          validate={(values) => validate(values, contactUsers.length === 0 || usersForTenantLoading, id)}
          innerRef={formRef}
        >
          <Form>
            <FormSection title="Site Details" className="space-bottom">
              <div className="d-flex">
                <div className="flex space-right">
                  <FormField
                    labelText="Site Company"
                    disabled={!!id && !hasGlobalTenantAccess}
                    InputComp={CompanySelectDropDown}
                    placeholder="Select Company"
                    name="tenantIdentifier"
                    extraOnChange={(tenantId) => {
                      setContactTenantId(tenantId);
                      dispatch(getAvailableUsersForTenant({ tenantId }));

                      // if (formRef.current) {
                      //   formRef.current.setFieldValue('mainContact', undefined);
                      //   formRef.current.setFieldValue('secondContact', undefined);
                      //   formRef.current.setFieldValue('thirdContact', undefined);
                      //   formRef.current.setFieldValue('fourthContact', undefined);
                      // }
                    }}
                  />
                  <FormField name="name" labelText="Site Name" placeholder="Input here" />
                  <FormField name="address" labelText="Site Address" placeholder="Input here.." />
                </div>

                <div className="flex space-left">
                  <Uploader
                    accept="image/*"
                    beforeUpload={() => Promise.reject()}
                    className="w-200px h-200px"
                    maxCount={1}
                    ref={imageRef}
                  />
                </div>
              </div>
            </FormSection>

            <FormSection title="Contacts">
              <div className="d-flex">
                <div className="flex space-right">
                  <FormField
                    name="mainContact"
                    labelText="First Contact"
                    placeholder="Input here.."
                    InputComp={AvailableUsers}
                    extraOnChange={(v) => filterRestUsers(v, 'mainContact')}
                    users={filteredUsersForTenant.mainContact.list}
                    disabled={filteredUsersForTenant.mainContact.list.length === 0 || usersForTenantLoading}
                  />
                  <FormField
                    name="secondContact"
                    labelText="Second Contact"
                    placeholder="Input here.."
                    InputComp={AvailableUsers}
                    extraOnChange={(v) => filterRestUsers(v, 'secondContact')}
                    users={filteredUsersForTenant.secondContact.list}
                    disabled={filteredUsersForTenant.secondContact.list.length === 0 || usersForTenantLoading}
                  />
                </div>

                <div className="flex space-left">
                  <FormField
                    name="thirdContact"
                    labelText="Third Contact"
                    placeholder="Input here.."
                    InputComp={AvailableUsers}
                    extraOnChange={(v) => filterRestUsers(v, 'thirdContact')}
                    users={filteredUsersForTenant.thirdContact.list}
                    disabled={filteredUsersForTenant.thirdContact.list.length === 0 || usersForTenantLoading}
                  />
                  <FormField
                    name="fourthContact"
                    labelText="Fourth Contact"
                    placeholder="Input here.."
                    InputComp={AvailableUsers}
                    extraOnChange={(v) => filterRestUsers(v, 'fourthContact')}
                    users={filteredUsersForTenant.fourthContact.list}
                    disabled={filteredUsersForTenant.fourthContact.list.length === 0 || usersForTenantLoading}
                  />
                </div>
              </div>
            </FormSection>

            <div className="d-flex jc-end space-top space-top-p space-bottom">
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </div>
          </Form>
        </Formik>
      ) : (
        <Spin spinning>
          <div style={{ width: '100%', height: 200 }} />
        </Spin>
      )}
    </SubPageLayout>
  );
};

const initialValue = {
  name: '',
  address: '',
  tenantIdentifier: undefined,
  mainContact: undefined,
  secondContact: undefined,
  thirdContact: undefined,
  fourthContact: undefined,
};

const validate = async (values, hasAvailableUsers, isEditing) => {
  const errors = {};
  const { name, address, tenantIdentifier, mainContact } = values;

  if (!name || !name.trim()) {
    errors.name = 'Required';
  }
  if (!address || !address.trim()) {
    errors.address = 'Required';
  }
  if (!tenantIdentifier) {
    errors.tenantIdentifier = 'Required';
  }
  if (!mainContact && !isEditing) {
    errors.mainContact = 'Required';
  }
  if (hasAvailableUsers && !isEditing) {
    errors.mainContact = "You don't have available user in this company";
  }

  return errors;
};

export default SitesAdd;